<script setup>
import {usePortalContentAndDesignStore} from "../../../../stores/partners/portalContentAndDesignStore";
import {defineProps, watch} from "vue";
// Only the families we self-host in public/fonts can be offered here.
import brandFonts from "../../../../../fonts/families.json";

const props = defineProps({
    authView: {
        type: Boolean,
        default: false
    }
});

const portalContentAndDesignStore =  usePortalContentAndDesignStore()

// Helper to apply primary color to all portal color settings
const applyPrimaryColorToAllSettings = (color) => {
    // Affiliate portal settings
    portalContentAndDesignStore.portalLayoutSettings.portal_layout_button_color = color;
    portalContentAndDesignStore.portalLayoutSettings.portal_layout_link_color = color;
    portalContentAndDesignStore.portalLayoutSettings.public_page_link_color = color;
    portalContentAndDesignStore.portalLayoutSettings.public_page_content_link_color = color;
    portalContentAndDesignStore.portalLayoutSettings.public_page_content_button_color = color;
    // Referral portal settings
    portalContentAndDesignStore.portalLayoutSettings.referral_portal_link_color = color;
    portalContentAndDesignStore.portalLayoutSettings.referral_portal_content_link_color = color;
    portalContentAndDesignStore.portalLayoutSettings.referral_portal_content_button_color = color;
};

const handlePortalLayoutBrandPrimaryColorChange = (event) => {
    portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color = event.finalValue
    if (portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_primary_color_as_partner_and_pages_primary) {
        applyPrimaryColorToAllSettings(event.finalValue);
    }
};

// When "Use as primary color" toggle changes, apply the current brand color to all settings
watch(() => portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_primary_color_as_partner_and_pages_primary, (newValue) => {
    if (newValue) {
        const brandColor = portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_primary_color || 'teal-600';
        applyPrimaryColorToAllSettings(brandColor);
    }
});

// Font application (documentElement font-family + Google Fonts <link>) is handled
// centrally in the always-mounted PortalContentAndDesignInitializer so it stays in
// sync live regardless of which Live Designer tab is open.

// Delete handlers - clear path (src is cleared automatically by FileUpload component)
const handleLogoDelete = () => {
    portalContentAndDesignStore.srcRoutes.portal_layout_file_logo_path = null;
};

const handleSymbolDelete = () => {
    portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol_path = null;
};

const handleFaviconDelete = () => {
    portalContentAndDesignStore.srcRoutes.portal_layout_file_favicon_path = null;
};

</script>

<template>
    <file-upload
        v-model:src="portalContentAndDesignStore.srcRoutes.portal_layout_file_logo"
        :path="portalContentAndDesignStore.srcRoutes.portal_layout_file_logo_path"
        no_src="/images/upload-image.png"
        accept="image/*"
        title="Logo"
        name="portal_layout_file_logo"
        :upload_url="portalContentAndDesignStore.getFileUploadRoute"
        :delete_url="portalContentAndDesignStore.getFileDeleteRoute"
        @delete:done="handleLogoDelete"
    ></file-upload>

    <file-upload
        v-model:src="portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol"
        :path="portalContentAndDesignStore.srcRoutes.portal_layout_file_symbol_path"
        no_src="/images/upload-image.png"
        accept="image/*"
        title="Symbol"
        name="portal_layout_file_symbol"
        :upload_url="portalContentAndDesignStore.getFileUploadRoute"
        :delete_url="portalContentAndDesignStore.getFileDeleteRoute"
        @delete:done="handleSymbolDelete"
    ></file-upload>

    <file-upload
        v-model:src="portalContentAndDesignStore.srcRoutes.portal_layout_file_favicon"
        :path="portalContentAndDesignStore.srcRoutes.portal_layout_file_favicon_path"
        no_src="/images/upload-image.png"
        accept="image/*"
        title="Favicon"
        name="portal_layout_file_favicon"
        :upload_url="portalContentAndDesignStore.getFileUploadRoute"
        :delete_url="portalContentAndDesignStore.getFileDeleteRoute"
        @delete:done="handleFaviconDelete"
    ></file-upload>

    <select-field
        label="Font family"
        id="portal_layout_brand_font_family"
        name="portal_layout_brand_font_family"
        :model-value="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family || brandFonts.default"
        @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_font_family = $event"
    >
        <option v-for="(font, value) in brandFonts.fonts"
                :key="value"
                :value="value"
                :selected="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_font_family === value">
            {{ font.family }}
        </option>
    </select-field>

    <div>
        <radio-group-inline-color
            label="Brand color"
            name="portal_layout_brand_primary_color"
            :model-value="portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_primary_color || 'teal-600'"
            @picked="handlePortalLayoutBrandPrimaryColorChange($event)"
            :options="[
                { value: 'indigo-700', label: 'bg-indigo-700', label_is_class: true},
                { value: 'blue-700', label: 'bg-blue-700', label_is_class: true},
                { value: 'cyan-700', label: 'bg-cyan-700', label_is_class: true},
                { value: 'teal-600', label: 'bg-teal-600', label_is_class: true},
                { value: 'black', label: 'bg-black', label_is_class: true},
                { value: 'custom', label: 'Custom', custom: { name: 'portal_layout_brand_primary_color', placeholder: '', type: 'input' }},
            ]"
        >
        </radio-group-inline-color>
        <toggle
            size="medium"
            class="text-gray-500 mt-3.5"
            name="portal_layout_brand_primary_color_as_partner_and_pages_primary"
            :model-value="!!portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_primary_color_as_partner_and_pages_primary"
            @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_layout_brand_primary_color_as_partner_and_pages_primary = $event"
            title="Use as primary color"
        >
        </toggle>
    </div>
</template>

